import React from 'react';
import { TargetIcon, VoteIcon, ShieldOffIcon, MicOffIcon } from 'lucide-react';
export function ProblemSection() {
  return <section className="py-16 px-4 bg-zinc-950 relative border-y border-gold-900/20" data-id="element-274">
      <div className="max-w-5xl mx-auto space-y-20" data-id="element-275">
        {/* The Problem */}
        <div className="grid md:grid-cols-2 gap-10 items-center" data-id="element-276">
          <div className="space-y-5 order-2 md:order-1" data-id="element-277">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" data-id="element-278">
              The collapse of civilized dialogue
            </h2>
            <div className="w-16 h-1 bg-gold-gradient" data-id="element-279"></div>
            <p className="text-xl text-zinc-400 leading-relaxed" data-id="element-280">
              We are witnessing the collapse of civilized dialogue. Today,
              defending family, faith, and freedom of speech doesn't make you a
              citizen with different ideas — it makes you a social{' '}
              <span className="text-gold-400 font-mono bg-gold-950/50 px-2 py-1 rounded" data-id="element-281">
                "target"
              </span>
              .
            </p>
            <p className="text-xl text-zinc-400 leading-relaxed" data-id="element-282">
              The problem isn't just the external attack; it's the{' '}
              <strong className="text-gold-100" data-id="element-283">Pedagogy of Fear</strong>. We've
              been trained to self-censor, to doubt our own convictions, and to
              accept that truth is relative.
            </p>
            <div className="p-6 glass-card rounded-xl border-l-4 border-l-gold-500 relative overflow-hidden" data-id="element-284">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/10 blur-3xl rounded-full" data-id="element-285"></div>
              <p className="text-lg text-zinc-300 italic relative z-10" data-id="element-286">
                "While the elites decide what counts as 'misinformation,' the
                voice of the common citizen is buried under layers of software
                and hate speech laws. The Charlie Kirk case is the darkest
                mirror of our era: if they can try to erase those with millions
                of followers, what chance do you think you have when they decide
                your opinion is no longer permitted?"
              </p>
            </div>
          </div>
          <div className="order-1 md:order-2 flex justify-center" data-id="element-287">
            <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-full border border-gold-900/30 flex items-center justify-center bg-gold-950/10 shadow-[0_0_50px_rgba(201,168,76,0.05)]" data-id="element-288">
              <div className="absolute inset-0 rounded-full border border-gold-500/20 animate-spin-slow" data-id="element-289"></div>
              <div className="absolute inset-8 rounded-full border border-gold-700/30 animate-spin-reverse-slow" data-id="element-290"></div>
              <TargetIcon className="w-24 h-24 text-gold-600/60 drop-shadow-[0_0_15px_rgba(201,168,76,0.5)]" strokeWidth={1} data-id="element-291" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" data-id="element-292"></div>
            </div>
          </div>
        </div>

        {/* Why what you tried didn't work */}
        <div className="space-y-10" data-id="element-293">
          <div className="text-center space-y-4" data-id="element-294">
            <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-295">
              Why Everything You've Tried Has Failed
            </h2>
            <p className="text-gold-500 font-mono uppercase tracking-widest text-sm" data-id="element-296">
              Systemic failure analysis
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-297">
            <div className="glass-card p-8 rounded-2xl hover:border-gold-500/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(201,168,76,0.1)]" data-id="element-298">
              <div className="w-14 h-14 rounded-full bg-gold-950/50 border border-gold-800/50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500" data-id="element-299">
                <VoteIcon className="w-8 h-8 text-gold-500" data-id="element-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-301">
                Voting every four years is not enough
              </h3>
              <p className="text-zinc-400 leading-relaxed text-lg" data-id="element-302">
                Real change doesn't happen at the ballot box if the culture has
                already been infected in classrooms and entertainment.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl hover:border-gold-500/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(201,168,76,0.1)]" data-id="element-303">
              <div className="w-14 h-14 rounded-full bg-gold-950/50 border border-gold-800/50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500" data-id="element-304">
                <ShieldOffIcon className="w-8 h-8 text-gold-500" data-id="element-305" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-306">
                Silence is not protection
              </h3>
              <p className="text-zinc-400 leading-relaxed text-lg" data-id="element-307">
                Believing that "if I stay out of politics, nothing will happen
                to me" is a dangerous fantasy; the system will eventually come
                for everything you believe in.
              </p>
            </div>

            <div className="glass-card p-8 rounded-2xl hover:border-gold-500/50 transition-all duration-500 group hover:-translate-y-2 hover:shadow-[0_10px_40px_rgba(201,168,76,0.1)]" data-id="element-308">
              <div className="w-14 h-14 rounded-full bg-gold-950/50 border border-gold-800/50 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-500" data-id="element-309">
                <MicOffIcon className="w-8 h-8 text-gold-500" data-id="element-310" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-311">
                Debating by their rules means losing
              </h3>
              <p className="text-zinc-400 leading-relaxed text-lg" data-id="element-312">
                You can't win a game where your opponent has the power to turn
                off your microphone at any moment. You need a new strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}